<template>
    <div>
        <el-autocomplete
            v-model="valueInput"
            :disabled="disabled"
            :fetch-suggestions="fetchSuggestions"
            :placeholder="$lang('el.autocomplete.placeholder')"
            popper-class="a-popover__loader"
            class="search__caret-inside"
            v-bind="$attrs"
            v-click="closeList"
            :popper-append-to-body="false"
            :debounce="currentDebounce"
            :dusk="dusk"
            @input="closeList"
            @blur="handlerBlur"
            @focus="$emit('focus', $event)"
            @select="handlerSelect">
            <i :class="classCaret"
               slot="suffix"
               @click.prevent="handleIconClick">
            </i>
            <template slot-scope="{item}">
                <slot v-bind:item="item.data"></slot>
            </template>
        </el-autocomplete>
    </div>
</template>

<script>
    export default {
        name: "SearchAutocomplete",
        props: {
            value: {
                required: true
            },
            valueKey: {
                type: String|Function,
                required: true
            },
            label: {
                type: String|Function,
                required: true
            },
            stateName: {
                type: String,
                required: true
            },
            searchMethod: {
                type: Function,
                required: true
            },
            searchParams: {
                type: Object|undefined,
                default: undefined,
            },
            excludeFromFullSearch: {
                type: Array,
                default: () => []
            },
            disabled: {
                type: Boolean,
                default: false
            },
            dusk: {
                type: String,
                default: 'autocomplete'
            }
        },
        data: () => ({
            valueInput: undefined,
            selectedValue: undefined,
            previousList: [],
            lastSearchList: [],
            debounce: {
                previousList: 300,
                other: 800,
            },
            currentDebounce: 300,
            lastSearchId: null,
            openList: false,
            classCaret: 'el-select__caret el-input__icon el-icon-arrow-up',
            cb: () => {}
        }),
        directives: {
            click: {
                bind(el, binding) {
                    const element = el.querySelector('.el-input__inner');

                    element.addEventListener("click", function () {
                        binding.value();
                    });
                }
            }
        },
        computed: {
            itemList() {
                return this.$store.getters[this.stateName] ?? [];
            },
        },
        watch: {
            value(val) {
                if (val) {
                    this.selectValue(val);
                } else {
                    this.$set(this, 'selectedValue',  val);
                    this.$set(this, 'valueInput', val);
                }
            },
            selectedValue(val) {
                this.$emit('input', val);
            },
            searchParams(val) {
                if (!val || this.disabled) {
                    return;
                }

                this.selectedValue = undefined;
                this.previousList = [];

                this.$set(this, 'valueInput', undefined);

                // Обновлеем список выбора на основе новых параметров поиска
                this.searchItems(null)
                    .then(() => {
                        // сохраняем копию
                        this.previousList = this.itemList.map(
                            item => this.prepareData(item)
                        );
                    });
            },
            openList(val) {
                this.$nextTick(() => {
                    this.classCaret = val
                        ? 'el-select__caret el-input__icon el-icon-arrow-up is-active'
                        : 'el-select__caret el-input__icon el-icon-arrow-up';
                });
            },
        },
        methods: {
            handleIconClick(ev) {
                this.openList
                    ? this.closeList()
                    : ev.target.closest('.el-input').querySelector('.el-input__inner').focus();
            },
            handlerSelect(val) {
                this.selectedValue = this.getValue(val.data);
            },
            handlerBlur() {
                this.classCaret = 'el-select__caret el-input__icon el-icon-arrow-up';
            },
            closeList() {
                if (this.openList) {
                    this.cb([]);

                    this.openList = false;
                } else {
                    this.cb(this.lastSearchList ?? this.previousList);

                    this.openList = true;
                }
            },
            fetchSuggestions(name, cb) {
                this.cb = cb;

                if (!name) {
                    this.handlerEmptyName(cb);
                    return;
                }

                this.$set(this, 'currentDebounce', this.debounce.other);

                let currentSearchId = Date.now();

                this.$set(this, 'lastSearchId', currentSearchId);

                this.searchItems(name, true).then((response) => {
                    if (currentSearchId !== this.lastSearchId) {
                        return
                    }

                    this.lastSearchList = response.data.map(
                        item => this.prepareData(item)
                    );

                    cb(this.lastSearchList);

                    this.openList = true;
                }).catch(() => {
                    cb([]);
                });
            },
            handlerEmptyName(cb) {
                this.$set(this, 'currentDebounce', this.debounce.previousList);

                this.$emit('input', undefined);

                cb(this.previousList);

                this.lastSearchList = this.previousList;

                this.openList = true;
            },
            searchItems(name, fullSearch = false) {
                let searchParams = this.searchParams;

                if (fullSearch) {
                    searchParams = this.getParamsForFullSearch();
                    searchParams.maxCount = true;
                }

                let search = {
                    ...searchParams,
                    ...{ name: name }
                };

                return this.searchMethod(search).catch((error) => {
                    this.$appNotify.error(
                        error.hasOwnProperty('data')
                            ? error.data
                            : error
                    );
                })
            },
            /**
             * Получить объект с параметрами для полного поиска,
             * исключая параметры указанные в excludeFromFullSearch
             * @returns {{}}
             */
            getParamsForFullSearch() {
                let searchParams = {};

                if (typeof this.searchParams === 'undefined') {
                    return searchParams;
                }

                // Перебераем параметры для поиска
                Object.keys(this.searchParams).forEach((name) => {
                    // Если параметра нет в спике исключений
                    if (this.excludeFromFullSearch.indexOf(name) < 0) {
                        // сохраняем его
                        searchParams[name] = this.searchParams[name];
                    }
                });

                return searchParams;
            },
            prepareData(data) {
                return {value: this.getLabel(data), data: data};
            },
            getValue(data)  {
                return typeof this.valueKey === 'function'
                    ? this.valueKey(data)
                    : data[this.valueKey]
            },
            getLabel(data) {
                return typeof this.label === 'function'
                    ? this.label(data)
                    : data[this.label]
            },
            selectValue(value) {
                this.itemList.forEach((data) => {
                    if (this.getValue(data) === value) {
                        this.$set(this, 'valueInput', this.getLabel(data));
                        return;
                    }
                });
            }
        },
        mounted() {
            // Инициализируем/загружаем первичный список выбора
            this.searchItems(null).then(() => {
                // сохраняем копию
                this.previousList = this.itemList.map(
                    item => this.prepareData(item)
                );
            });
        }
    }
</script>
